import { type ReactNode, useCallback, useState } from 'react';

import type {
	ExternalContextBrowserProps,
	OnPromptInputContextAvailable,
	OnShowContextBrowser,
	PromptInputContext,
	PromptInputContextMenuItem,
} from '../types';

export const useExternalContextBrowser = ({
	contextProviders = [],
	onContextBrowserShow,
	onContextAvailable,
}: ExternalContextBrowserProps = {}) => {
	const [currentBrowser, setCurrentBrowser] = useState<ReactNode | null>(null);
	const [currentContexts, setCurrentContexts] = useState<PromptInputContext[]>([]);

	const handleShowContextBrowser: OnShowContextBrowser = useCallback(
		(browser: ReactNode | null) => {
			setCurrentBrowser(browser);
			onContextBrowserShow?.(browser);
		},
		[onContextBrowserShow],
	);

	const handlePromptInputContextAvailable: OnPromptInputContextAvailable = useCallback(
		(contexts: PromptInputContext[]) => {
			const contextMap = new Map<string, PromptInputContext>();

			// add existing contexts first
			currentContexts.forEach((context) => {
				contextMap.set(context.key, context);
			});

			// add/override with new contexts
			contexts.forEach((context) => {
				contextMap.set(context.key, context);
			});

			const updatedContexts = Array.from(contextMap.values());
			setCurrentContexts(updatedContexts);
			onContextAvailable?.(updatedContexts);
		},
		[currentContexts, onContextAvailable],
	);

	const getMenuItems = useCallback((): PromptInputContextMenuItem[] => {
		return contextProviders
			.map((provider) =>
				provider.getMenuItem(handleShowContextBrowser, handlePromptInputContextAvailable),
			)
			.filter((item): item is PromptInputContextMenuItem => item !== null);
	}, [contextProviders, handleShowContextBrowser, handlePromptInputContextAvailable]);

	const clearContexts = useCallback(() => {
		setCurrentContexts([]);
		onContextAvailable?.([]);
	}, [onContextAvailable]);

	const removeContext = useCallback(
		(contextKey: string) => {
			const updatedContexts = currentContexts.filter((context) => context.key !== contextKey);
			setCurrentContexts(updatedContexts);
			onContextAvailable?.(updatedContexts);
		},
		[currentContexts, onContextAvailable],
	);

	return {
		currentBrowser,
		currentContexts,
		getMenuItems,
		clearContexts,
		removeContext,
		setCurrentBrowser,
	};
};
